import {
  Component,
  EventEmitter,
  HostListener,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { ButtonRowComponent } from '../../../../../shared/components/button-row/button-row.component';
import { catchError, EMPTY, switchMap } from 'rxjs';
import { LoadingComponent } from '../../../../../shared/components/loading/loading.component';
import { SepTableSelectionService } from '../../../../../shared/components/sep-table/services/sep-table-selection.service';

import { LicenseInfoCardComponent } from '../../../../../domains/payment/subscription/components/license-info-card/license-info-card.component';
import { EnrollmentContainerBase } from '../EnrollmentContainerBase';
import { LearningPlanDataService } from '../../../../../domains/academy/learning-plan/services/learning-plan-data.service';
import { LearningPlanService } from '../../../../../domains/academy/learning-plan/services/learning-plan.service';
import { CustomPortalError } from '../../../../../core/errors/models/CustomPortalError';
import { SnackbarService } from '../../../../../core/services/snackbar.service';
import { LearningPlanEnrollmentTableComponent } from '../../../../../domains/academy/learning-plan/components/learning-plan-enrollment-table/learning-plan-enrollment-table.component';
import { EnrollmentNotificationService } from '../../../../../core/services/enrollment-notification.service';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-learning-plan-enrollment-container',
  imports: [
    TranslocoDirective,
    ButtonRowComponent,
    LoadingComponent,
    LicenseInfoCardComponent,
    LearningPlanEnrollmentTableComponent,
    MatIcon,
  ],
  providers: [SepTableSelectionService],
  templateUrl: './learning-plan-enrollment-container.component.html',
  styleUrl: './learning-plan-enrollment-container.component.css',
})
export class LearningPlanEnrollmentContainerComponent
  extends EnrollmentContainerBase
  implements OnInit
{
  private readonly learningPlanDataService = inject(LearningPlanDataService);
  private readonly learningPlanService = inject(LearningPlanService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly enrollmentNotificationService = inject(
    EnrollmentNotificationService,
  );

  @Output() userCreationButtonClickEvent: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() abortButtonClickEvent: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() studentSuccessfullyEnrolledEvent: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() addLicensesButtonClickEvent: EventEmitter<void> =
    new EventEmitter<void>();

  constructor() {
    super();
  }
  ngOnInit(): void {
    this.getStudentsEligibleForEnrollment();
    this.getAvailableLicenses();
    this.observeTableSelection();
  }

  getStudentsEligibleForEnrollment() {
    const learningPlanId =
      this.learningPlanDataService.learningPlanInstanceId();
    if (learningPlanId) {
      this.learningPlanService
        .getLearningPlanEnrollments(learningPlanId)
        .pipe(
          switchMap((res) => {
            const enrolledStudents = res.students;
            return this.getEligibleStudents(enrolledStudents);
          }),
          catchError((error: CustomPortalError) => {
            this.snackbarService.error(error.errorType);
            return EMPTY;
          }),
        )
        .subscribe({
          next: () => {
            // After loading students, apply sorting and initialize filtered list
            if (this.studentsEligibleForEnrollment) {
              try {
                this.studentsEligibleForEnrollment = this.sortStudents(
                  this.studentsEligibleForEnrollment,
                );
              } catch (error) {
                console.error('Error sorting students:', error);
                // If sorting fails, keep original order
              }
              this.filteredStudents = this.studentsEligibleForEnrollment;
            }
          },
          error: (error) => {
            console.error('Error loading eligible students:', error);
          },
        });
    }
  }

  enrollStudents() {
    this.isLoading = true;
    const learningPlanId =
      this.learningPlanDataService.learningPlanInstanceId();
    const students = this.selectedStudents.map((student) => ({
      id: student.id,
    }));
    if (students.length && learningPlanId) {
      this.learningPlanService
        .enrollStudentsToLearningPlan(students, learningPlanId)
        .subscribe({
          next: () => {
            this.isLoading = false;
            this.learningPlanDataService.getLearningPlanDetails(learningPlanId);
            this.enrollmentNotificationService.setEnrollmentChanged();
            this.analyticsService.track('students_enrolled', {
              courseId: learningPlanId,
              students: students,
              participantsAddedCount: students.length,
              numberOfAssignedLicences: this.pendingLicenseAssignments,
            });
            this.studentSuccessfullyEnrolledEvent.emit();
          },
          complete: () => {
            this.snackbarService.success(
              'portal.trainings.successfulEnrollmentSnackbar',
              {
                count: students.length,
                training: this.languageService.translateOnRuntime(
                  'portal.trainings.trainingNameKeys.' +
                    this.learningPlanDataService.learningPlanName(),
                ),
              },
            );
          },
          error: (error: CustomPortalError) => {
            this.isLoading = false;
            this.snackbarService.error(error.errorType);
          },
        });
    }
  }

  @HostListener('window:keydown.enter', ['$event'])
  onEnterKey() {
    if (this.selectedStudents.length > 0 && this.canEnroll) {
      this.enrollStudents();
    }
  }
}
